import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useFormik } from "formik";
import * as Yup from "yup";
import Table from "../../core/common/dataTable/index";
import CommonSelect, { Option } from "../../core/common/commonSelect";
import {
  activeList,
  SORT_OPTIONS,
} from "../../core/common/selectoption/selectoption";
import { all_routes } from "../router/all_routes";
import TooltipOption from "../../core/common/tooltipOption";
import {
  createUser,
  getAllUsers,
  updateUser,
  deleteUser,
  loginAsUser,
  USER,
  USER_PAYLOAD,
} from "../../service/user/index";
import { useReactToPrint } from "react-to-print";
import { getAllRoles } from "../../service/roles-permission";
import { useSelector } from "react-redux";
import { selectUser } from "../../core/data/redux/slice/user";
import { selectBranches, selectCurrentBranch } from "../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../utils/campusLogos";
import PhoneInput from "../../components/PhoneNumber";

interface NewUserData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
  phoneNumber: string;
  status: string;
  department?: string;
  designation?: string;
  joiningDate?: string;
}

interface Role {
  _id: string;
  name: string;
  isEmployee?: boolean;
}

interface UserRole {
  _id: string;
  name: string;
  permissions: string[];
  createdBy?: string;
  isActive: boolean;
  isEmployee: boolean;
  createdAt: string;
  updatedAt: string;
}

const getRoleId = (role: string | UserRole): string => {
  if (typeof role === "object" && role !== null && role._id) {
    return role._id;
  }
  return role as string;
};

const getRoleName = (role: string | { name?: string } | null | undefined): string => {
  if (typeof role === "object" && role !== null && role.name) {
    return role.name;
  }
  return String(role || "");
};

const canRoleLoginAsUser = (roleName: string): boolean =>
  ["owner", "super admin", "super-admin", "superadmin", "admin"].includes(
    roleName.trim().toLowerCase()
  );

const Manageusers = () => {
  const user = useSelector(selectUser);
  const currentBranch = useSelector(selectCurrentBranch);
  const routes = all_routes;
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const [users, setUsers] = useState<USER[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [pagination, setPagination] = useState({ limit: 10, page: 1 });
  const [paginationData, setPaginationData] = useState<any>();
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);
  const [showPassword, setShowPassword] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<{
    status?: Option;
    role?: Option;
  } | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<{
    status?: string;
    role?: string;
  } | null>(null);
  const [editingUser, setEditingUser] = useState<USER | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [loginAsUserId, setLoginAsUserId] = useState<string | null>(null);

  // Multi-branch: "Campus Access" modal state.
  const branches = useSelector(selectBranches);
  const [accessUser, setAccessUser] = useState<USER | null>(null);
  const [accessHome, setAccessHome] = useState<string>("");
  const [accessExtra, setAccessExtra] = useState<string[]>([]);
  const [accessSaving, setAccessSaving] = useState(false);

  const branchId = (b: unknown): string =>
    typeof b === "object" && b !== null ? String((b as { _id?: string })._id ?? "") : String(b ?? "");

  const openCampusAccess = (u: USER) => {
    setAccessUser(u);
    setAccessHome(branchId(u.branch));
    setAccessExtra((u.accessibleBranches || []).map(branchId).filter(Boolean));
  };

  const saveCampusAccess = async () => {
    if (!accessUser) return;
    setAccessSaving(true);
    try {
      // Home campus is always accessible, so drop it from the "extra" list.
      const extra = accessExtra.filter((id) => id && id !== accessHome);
      await updateUser(accessUser._id, {
        branch: accessHome,
        accessibleBranches: extra,
      } as Partial<USER_PAYLOAD>);
      toast.success("Campus access updated");
      setAccessUser(null);
      await fetchUsers();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update access");
    } finally {
      setAccessSaving(false);
    }
  };

  const roleOptions = roles.map((role) => ({
    label: role.name,
    value: role._id,
  }));

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await getAllRoles({
        page: 1,
        limit: 100,
        sortBy: "name",
        order: "asc",
        context: "create",
      });
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load roles");
    } finally {
      setRolesLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name cannot exceed 50 characters")
      .matches(
        /^[a-zA-Z\s]+$/,
        "First name can only contain letters and spaces"
      )
      .required("First name is required"),
    lastName: Yup.string()
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name cannot exceed 50 characters")
      .matches(/^[a-zA-Z\s]+$/, "Last name can only contain letters and spaces")
      .required("Last name is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      )
      .required("Password is required"),
    role: Yup.string().required("Please select a role"),
    phoneNumber: Yup.string().required("Phone Number is required"),
    status: Yup.string()
      .oneOf(["active", "inactive"], "Invalid status")
      .required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "",
      phoneNumber: "",
      status: "active",
      department: "",
      designation: "",
      joiningDate: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleCreateUser(values);
    },
  });

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleId = e.target.value;
    formik.handleChange(e);

    const selected = roles.find((role) => role._id === roleId);
    setSelectedRole(selected || null);
  };

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    setAppliedFilters({
      status: filters?.status?.value as string,
      role: filters?.role?.value as string,
    });
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getAllUsers({
        ...pagination,
        ...appliedFilters,
        searchText: searchText,
        ...sortBy.value,
      });
      setUsers(response.data);
      setPaginationData(response.pagination);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [pagination, appliedFilters, searchText, sortBy]);

  const resetUserForm = () => {
    formik.resetForm();
    setSelectedRole(null);
  };

  const handleCreateUser = async (userData: NewUserData) => {
    if (loading) return;
    setLoading(true);

    try {
      const apiData: any = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: userData.password,
        role: userData.role,
        phoneNumber: userData.phoneNumber,
        status: userData.status,
        // Home campus is stamped server-side from the selected branch
        // (x-branch header) — no hard-coded default.
      };

      if (userData.department) {
        apiData.department = userData.department;
      }
      if (userData.designation) {
        apiData.designation = userData.designation;
      }
      if (userData.joiningDate) {
        apiData.joiningDate = userData.joiningDate;
      }

      await createUser(apiData);

      await fetchUsers();
      toast.success("User created successfully!");
      const el = document.getElementById("add_user");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }

      resetUserForm();
    } catch (error: any) {
      if (
        error.response?.status === 400 &&
        error.response?.data?.message === "Email already in use"
      ) {
        toast.error("Email already in use");
      } else if (
        error.response?.status === 400 &&
        error.response?.data?.message === "Mobile number already in use"
      ) {
        toast.error("Mobile number already in use");
      } else if (
        error.response?.status === 403 &&
        error.response?.data?.message === "Cannot create owner role"
      ) {
        toast.error("Cannot create user with owner role");
      } else if (
        error.response?.status === 403 &&
        error.response?.data?.message?.includes("Cannot create")
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || "Failed to create user");
      }
      console.error("Error creating user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setLoading(true);

    try {
      const roleId = getRoleId(editingUser.role);

      const updateData: any = {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        phoneNumber: editingUser.phoneNumber,
        status: editingUser.status,
        email: editingUser.email,
        role: roleId,
      };

      if (editingUser.password) {
        updateData.password = editingUser.password;
      }

      if (editingUser.department) {
        updateData.department = editingUser.department;
      }
      if (editingUser.designation) {
        updateData.designation = editingUser.designation;
      }
      if (editingUser.joiningDate) {
        updateData.joiningDate = editingUser.joiningDate;
      }

      await updateUser(editingUser._id, updateData);

      toast.success("User updated successfully!");
      const el = document.getElementById("edit_user");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }

      await fetchUsers();
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error("Not authorized to update this user");
      } else {
        toast.error(error.response?.data?.message || "Failed to update user");
      }
      console.error("Error updating user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    setLoading(true);

    try {
      await deleteUser(deleteUserId);
      toast.success("User deleted successfully!");

      const el = document.getElementById("delete_user");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }

      await fetchUsers();
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error("Not authorized to delete this user");
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || "Failed to delete user");
      }
      console.error("Error deleting user:", error);
    } finally {
      setLoading(false);
      setDeleteUserId(null);
    }
  };

  const handleEditClick = (user: USER) => {
    const roleId = getRoleId(user.role);
    const selected = roles.find((role) => role._id === roleId);

    let department = "";
    let designation = "";
    let joiningDate = "";

    if (
      user.employeeID &&
      typeof user.employeeID === "object" &&
      user.employeeID !== null
    ) {
      department = (user.employeeID as any).department || "";
      designation = (user.employeeID as any).designation || "";
      if ((user.employeeID as any).joiningDate) {
        try {
          const date = new Date((user.employeeID as any).joiningDate);
          if (!isNaN(date.getTime())) {
            joiningDate = date.toISOString().split("T")[0];
          }
        } catch (error) {
          console.error("Error formatting joining date:", error);
        }
      }
    } else {
      department = user.department || "";
      designation = user.designation || "";
      if (user.joiningDate) {
        try {
          const date = new Date(user.joiningDate);
          if (!isNaN(date.getTime())) {
            joiningDate = date.toISOString().split("T")[0];
          }
        } catch (error) {
          console.error("Error formatting joining date:", error);
        }
      }
    }

    const editingUserData = {
      ...user,
      department: department,
      designation: designation,
      joiningDate: joiningDate,
      role: roleId,
    };

    setEditingUser(editingUserData);
    setSelectedRole(selected || null);
  };

  const handleDeleteClick = (userId: string) => {
    setDeleteUserId(userId);
  };

  const canLoginAs = (record: USER) => {
    const currentRole = getRoleName(user.role);
    const targetRole = getRoleName(record.role);

    if (!canRoleLoginAsUser(currentRole)) return false;
    if (record._id === user._id) return false;
    if (record.status !== "active") return false;
    if (
      currentRole.trim().toLowerCase() !== "owner" &&
      canRoleLoginAsUser(targetRole)
    ) {
      return false;
    }

    return true;
  };

  const handleLoginAsUser = async (record: USER) => {
    if (!canLoginAs(record) || loginAsUserId) return;

    const newTab = window.open("", "_blank");
    if (!newTab) {
      toast.error("Please allow pop-ups to login as this user");
      return;
    }

    newTab.document.write("Opening user session...");
    setLoginAsUserId(record._id);
    try {
      const response = await loginAsUser(record._id);
      const targetUrl = `${window.location.origin}${routes.loginAsUser}?token=${encodeURIComponent(response.token)}`;
      newTab.opener = null;
      newTab.location.href = targetUrl;
    } catch (error: any) {
      newTab.close();
      toast.error(error?.response?.data?.message || "Failed to login as user");
    } finally {
      setLoginAsUserId(null);
    }
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditingUser((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "role") {
      const selected = roles.find((role) => role._id === value);
      setSelectedRole(selected || null);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      status: { label: "Select", value: "" },
      role: { label: "Select", value: "" },
    });
    setAppliedFilters(null);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page });
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setSortBy(item);
  };
  const handleExportExcel = async () => {
    let allUsers = users;
    try {
      const total = paginationData?.total || 0;
      if (total > users.length) {
        const response = await getAllUsers({
          page: 1,
          limit: total,
          ...appliedFilters,
          searchText: searchText,
          ...sortBy.value,
        });
        allUsers = response.data;
      }
    } catch (error) {
      console.error("Failed to fetch all users for export:", error);
    }

    const ws = XLSX.utils.json_to_sheet(
      allUsers.map((item) => ({
        ID:
          typeof item.employeeID === "object"
            ? item.employeeID.employeeID
            : item.employeeID,
        "First Name": item.firstName,
        "Last Name": item.lastName,
        Email: item.email,
        Role: typeof item.role === "object" ? item.role.name : item.role,
        "Phone Number": item.phoneNumber,
        Status: item.status,
        Department:
          typeof item.employeeID === "object"
            ? item.employeeID.department
            : item.department || "N/A",
        Designation:
          typeof item.employeeID === "object"
            ? item.employeeID.designation
            : item.designation || "N/A",
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "users.xlsx");
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const columns = [
    {
      title: "ID",
      dataIndex: ["employeeID", "employeeID"],
      render: (text: string, record: any, index: number) => {
        return (
          <>
            <Link to="#" className="link-primary">
              {record.employeeID?.employeeID || record._id}
            </Link>
          </>
        );
      },
    },
    {
      title: "Name",
      dataIndex: "firstName",
      render: (text: string, record: any) => (
        <>
          {record.firstName} {record.lastName}
        </>
      ),
      sorter: (a: any, b: any) => a.firstName.localeCompare(b.firstName),
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a: any, b: any) => a.email.localeCompare(b.email),
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (role: any) => {
        if (typeof role === "object" && role.name) {
          return (
            role.name.charAt(0).toUpperCase() + role.name.slice(1).toLowerCase()
          );
        }
        if (typeof role === "string") {
          return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
        }
        return role || "N/A";
      },
      sorter: (a: any, b: any) => {
        const roleA = typeof a.role === "object" ? a.role.name : a.role;
        const roleB = typeof b.role === "object" ? b.role.name : b.role;
        return (roleA || "").localeCompare(roleB || "");
      },
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      sorter: (a: any, b: any) =>
        (a.phoneNumber || "").localeCompare(b.phoneNumber || ""),
    },
    {
      title: "Department",
      dataIndex: "employeeID",
      render: (employeeID: any) => {
        if (typeof employeeID === "object" && employeeID !== null) {
          return employeeID.department || "N/A";
        }
        return "N/A";
      },
      sorter: (a: any, b: any) => {
        const deptA =
          typeof a.employeeID === "object" ? a.employeeID.department : "";
        const deptB =
          typeof b.employeeID === "object" ? b.employeeID.department : "";
        return (deptA || "").localeCompare(deptB || "");
      },
    },
    {
      title: "Designation",
      dataIndex: "employeeID",
      render: (employeeID: any) => {
        if (typeof employeeID === "object" && employeeID !== null) {
          return employeeID.designation || "N/A";
        }
        return "N/A";
      },
      sorter: (a: any, b: any) => {
        const desigA =
          typeof a.employeeID === "object" ? a.employeeID.designation : "";
        const desigB =
          typeof b.employeeID === "object" ? b.employeeID.designation : "";
        return (desigA || "").localeCompare(desigB || "");
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <>
          {text === "active" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              Active
            </span>
          ) : (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              Inactive
            </span>
          )}
        </>
      ),
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center gap-1">
          <button
            type="button"
            className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
            title="Login as User"
            disabled={!canLoginAs(record) || loginAsUserId === record._id}
            onClick={() => handleLoginAsUser(record)}
          >
            <i className="ti ti-login-2 fs-14" />
          </button>
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                {getRoleName(record.role) === "owner" ? (
                  <span
                    className="dropdown-item rounded-1 text-muted"
                    style={{
                      cursor: "not-allowed",
                      opacity: "0.6",
                    }}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit (Not allowed for owner)
                  </span>
                ) : (
                  <Link
                    className={`dropdown-item rounded-1 ${
                      user.permissions?.includes("user.edit") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_user"
                    onClick={(e) => {
                      if (
                        !user.permissions?.includes("user.edit") &&
                        user.permissions?.length
                      ) {
                        e.preventDefault();
                        return;
                      }
                      handleEditClick(record);
                    }}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                )}
              </li>
              {getRoleName(record.role) !== "owner" && (
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openCampusAccess(record);
                    }}
                  >
                    <i className="ti ti-building me-2" />
                    Campus Access
                  </Link>
                </li>
              )}
              <li>
                {getRoleName(record.role) === "owner" ? (
                  <span
                    className="dropdown-item rounded-1 text-muted"
                    style={{
                      cursor: "not-allowed",
                      opacity: "0.6",
                    }}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Delete (Not allowed for owner)
                  </span>
                ) : (
                  <Link
                    className={`dropdown-item rounded-1 ${
                      user.permissions?.includes("user.delete") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#delete_user"
                    onClick={(e) => {
                      e.preventDefault();
                      if (
                        !user.permissions?.includes("user.delete") &&
                        !!user.permissions?.length
                      ) {
                        return;
                      }
                      handleDeleteClick(record._id);
                    }}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Delete
                  </Link>
                )}
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Users</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">User Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Users
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchUsers}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className={`btn btn-primary ${
                    user.permissions?.includes("user.create") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  data-bs-toggle="modal"
                  data-bs-target="#add_user"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  {"Create User"}
                </Link>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Users List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Role</label>
                              <CommonSelect
                                className="select"
                                options={roleOptions}
                                value={filters?.role}
                                onChange={(roleItem) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    role: roleItem,
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={activeList}
                                value={filters?.status}
                                onChange={(statusItem) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    status: statusItem,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Users List</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                columns={columns}
                dataSource={users}
                loading={loading}
                setSearchText={setSearchText}
                pagination={{ ...paginationData, onChange: handlePageChange }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      <div className="modal fade" id="add_user">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{"Create User"}</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetUserForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={formik.handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          formik.touched.firstName && formik.errors.firstName
                            ? "is-invalid"
                            : ""
                        }`}
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      {formik.touched.firstName && formik.errors.firstName && (
                        <div className="invalid-feedback">
                          {formik.errors.firstName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          formik.touched.lastName && formik.errors.lastName
                            ? "is-invalid"
                            : ""
                        }`}
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      {formik.touched.lastName && formik.errors.lastName && (
                        <div className="invalid-feedback">
                          {formik.errors.lastName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className={`form-control ${
                          formik.touched.email && formik.errors.email
                            ? "is-invalid"
                            : ""
                        }`}
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="invalid-feedback">
                          {formik.errors.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control ${
                            formik.touched.password && formik.errors.password
                              ? "is-invalid"
                              : ""
                          }`}
                          name="password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          required
                        />
                        <span
                          className="position-absolute end-0 top-50 translate-middle-y me-3"
                          style={{ cursor: "pointer", zIndex: 5 }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i
                            className={`ti ti-${
                              showPassword ? "eye-off" : "eye"
                            }`}
                          />
                        </span>
                        {formik.touched.password && formik.errors.password && (
                          <div className="invalid-feedback">
                            {formik.errors.password}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Role</label>
                      <select
                        className={`form-control ${
                          formik.touched.role && formik.errors.role
                            ? "is-invalid"
                            : ""
                        }`}
                        name="role"
                        value={formik.values.role}
                        onChange={handleRoleChange}
                        onBlur={formik.handleBlur}
                        required
                        disabled={rolesLoading}
                      >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                          <option key={role._id} value={role._id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      {rolesLoading && (
                        <small className="text-muted">Loading roles...</small>
                      )}
                      {formik.touched.role && formik.errors.role && (
                        <div className="invalid-feedback">
                          {formik.errors.role}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Department (Optional)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="department"
                        value={formik.values.department}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter department"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Designation (Optional)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="designation"
                        value={formik.values.designation}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter designation"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Joining Date (Optional)
                      </label>
                      <div
                        className="input-icon position-relative"
                        onClick={() =>
                          (
                            document.querySelector(
                              'input[name="joiningDate"]'
                            ) as HTMLInputElement
                          )?.showPicker()
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="date"
                          className="form-control"
                          name="joiningDate"
                          value={formik.values.joiningDate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <PhoneInput
                        name="mobileNo"
                        value={formik.values.phoneNumber}
                        onChange={(value: any) =>
                          formik.setFieldValue("phoneNumber", value)
                        }
                        className={
                          formik.touched.phoneNumber &&
                          formik.errors.phoneNumber
                            ? "is-invalid"
                            : ""
                        }
                      />
                      {formik.touched.phoneNumber &&
                      formik.errors.phoneNumber ? (
                        <div className="text-danger">
                          {formik.errors.phoneNumber}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch-sm"
                          checked={formik.values.status === "active"}
                          onChange={(e) => {
                            formik.setFieldValue(
                              "status",
                              e.target.checked ? "active" : "inactive"
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetUserForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  id="modal_confirm"
                  disabled={
                    loading ||
                    !formik.isValid ||
                    rolesLoading ||
                    !(
                      user.permissions?.includes("user.create") ||
                      !user.permissions?.length
                    )
                  }
                >
                  {loading ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <div className="modal fade" id="edit_user">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">{"Edit User"}</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="firstName"
                        value={editingUser?.firstName || ""}
                        onChange={handleEditInputChange}
                        required
                        disabled={editingUser?.role === "owner"}
                        style={
                          editingUser?.role === "owner"
                            ? {
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }
                            : {}
                        }
                      />
                      {editingUser?.role === "owner" && (
                        <small className="text-muted">
                          Cannot edit owner's information
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="lastName"
                        value={editingUser?.lastName || ""}
                        onChange={handleEditInputChange}
                        required
                        disabled={editingUser?.role === "owner"}
                        style={
                          editingUser?.role === "owner"
                            ? {
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }
                            : {}
                        }
                      />
                      {editingUser?.role === "owner" && (
                        <small className="text-muted">
                          Cannot edit owner's information
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        name="email"
                        type="email"
                        className="form-control"
                        value={editingUser?.email || ""}
                        onChange={handleEditInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <div className="position-relative ">
                        <input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          className="form-control overflow-hidden   placeholder:overflow-hidden 
                   placeholder:text-ellipsis 
                   placeholder:whitespace-nowrap
                   placeholder:text-sm"
                          value={editingUser?.password || ""}
                          onChange={handleEditInputChange}
                          placeholder="Leave blank to keep current password"
                          style={{
                            fontSize: "12px",
                            paddingRight: "40px",
                          }}
                        />
                        <span
                          className="position-absolute end-0 top-50 translate-middle-y me-3"
                          style={{ cursor: "pointer", zIndex: 5 }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i
                            className={`ti ti-${
                              showPassword ? "eye-off" : "eye"
                            }`}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Role</label>
                      <select
                        name="role"
                        className="form-select"
                        value={
                          editingUser?.role ? getRoleId(editingUser.role) : ""
                        }
                        onChange={handleEditInputChange}
                        disabled={rolesLoading || editingUser?.role === "owner"}
                        style={
                          editingUser?.role === "owner" || rolesLoading
                            ? {
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }
                            : {}
                        }
                      >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                          <option key={role._id} value={role._id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      {rolesLoading && (
                        <small className="text-muted">Loading roles...</small>
                      )}
                      {editingUser?.role === "owner" && (
                        <small className="text-muted">
                          Cannot change owner's role
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Department (Optional)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="department"
                        value={editingUser?.department || ""}
                        onChange={handleEditInputChange}
                        placeholder="Enter department"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Designation (Optional)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="designation"
                        value={editingUser?.designation || ""}
                        onChange={handleEditInputChange}
                        placeholder="Enter designation"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Joining Date (Optional)
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        name="joiningDate"
                        value={editingUser?.joiningDate || ""}
                        onChange={handleEditInputChange}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        name="phoneNumber"
                        value={editingUser?.phoneNumber || ""}
                        onChange={handleEditInputChange}
                        disabled={editingUser?.role === "owner"}
                        style={
                          editingUser?.role === "owner"
                            ? {
                                backgroundColor: "#f8f9fa",
                                cursor: "not-allowed",
                              }
                            : {}
                        }
                      />
                      {editingUser?.role === "owner" && (
                        <small className="text-muted">
                          Cannot edit owner's information
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch-sm-edit"
                          checked={editingUser?.status === "active"}
                          onChange={(e) => {
                            if (editingUser?.role !== "owner") {
                              setEditingUser((prev: any) => ({
                                ...prev,
                                status: e.target.checked
                                  ? "active"
                                  : "inactive",
                              }));
                            }
                          }}
                          disabled={editingUser?.role === "owner"}
                          style={
                            editingUser?.role === "owner"
                              ? { cursor: "not-allowed" }
                              : {}
                          }
                        />
                      </div>
                    </div>
                    {editingUser?.role === "owner" && (
                      <small className="text-muted d-block mt-2">
                        Cannot change owner's status
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    loading || editingUser?.role === "owner" || rolesLoading
                  }
                  style={
                    editingUser?.role === "owner" || rolesLoading
                      ? {
                          backgroundColor: "#6c757d",
                          borderColor: "#6c757d",
                          cursor: "not-allowed",
                        }
                      : {}
                  }
                >
                  {loading ? "Updating..." : "Update User"}
                  {editingUser?.role === "owner" && " (Not allowed for owners)"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete User Modal */}
      <div className="modal fade" id="delete_user">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Delete User</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              {deleteUserId &&
              users.find((user) => user._id === deleteUserId)?.role ===
                "owner" ? (
                <div className="alert alert-warning" role="alert">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-alert-triangle fs-4 me-2"></i>
                    <h5 className="alert-heading mb-0">Cannot Delete Owner</h5>
                  </div>
                  <p className="mb-0 mt-2">
                    Users with the owner role cannot be deleted. Please contact
                    system administration if you need to make changes to an
                    owner account.
                  </p>
                </div>
              ) : (
                <p>
                  Are you sure you want to delete this user? This action cannot
                  be undone.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteUser}
                disabled={
                  loading ||
                  !!(
                    deleteUserId &&
                    users.find((user) => user._id === deleteUserId)?.role ===
                      "owner"
                  )
                }
                style={
                  deleteUserId &&
                  users.find((user) => user._id === deleteUserId)?.role ===
                    "owner"
                    ? {
                        backgroundColor: "#6c757d",
                        borderColor: "#6c757d",
                        cursor: "not-allowed",
                      }
                    : {}
                }
              >
                {loading ? "Deleting..." : "Delete User"}
                {deleteUserId &&
                  users.find((user) => user._id === deleteUserId)?.role ===
                    "owner" &&
                  " (Not allowed)"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Multi-branch: Campus Access modal */}
      {accessUser && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,.5)" }}
          onClick={() => setAccessUser(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Campus Access — {accessUser.firstName} {accessUser.lastName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setAccessUser(null)}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Owners and Admins can access all campuses automatically. For
                  other staff, set the home campus and tick any additional
                  campuses this user may access.
                </p>
                <div className="mb-3">
                  <label className="form-label">Home campus</label>
                  <select
                    className="form-select"
                    value={accessHome}
                    onChange={(e) => setAccessHome(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {branches.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.campusNo ? `${b.campusNo} — ` : ""}
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="form-label">
                  Additional campuses this user can access
                </label>
                {branches.length === 0 && (
                  <div className="text-muted small">No campuses found.</div>
                )}
                {branches.map((b) => {
                  const isHome = b._id === accessHome;
                  const checked = isHome || accessExtra.includes(b._id);
                  return (
                    <div className="form-check" key={b._id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`acc-${b._id}`}
                        checked={checked}
                        disabled={isHome}
                        onChange={(e) =>
                          setAccessExtra((prev) =>
                            e.target.checked
                              ? [...prev, b._id]
                              : prev.filter((x) => x !== b._id)
                          )
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`acc-${b._id}`}
                      >
                        {b.campusNo ? `${b.campusNo} — ` : ""}
                        {b.name}
                        {isHome ? " (home)" : ""}
                      </label>
                    </div>
                  );
                })}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-light"
                  onClick={() => setAccessUser(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={saveCampusAccess}
                  disabled={accessSaving || !accessHome}
                >
                  {accessSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manageusers;
